'use client';

import { useInputDragControl } from '../hooks/useInputDragControl';
import { Slot } from '@radix-ui/react-slot';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn, Input } from '..';

interface InputNumberContextValue {
  value: number | '';
  min?: number;
  max?: number;
  step: number;
  unit?: string;
  onValueChange: (value: number | '') => void;
}

const InputNumberContext = createContext<InputNumberContextValue | null>(null);

const useInputNumberContext = () => {
  const context = useContext(InputNumberContext);
  if (!context) {
    throw new Error(
      'InputNumber components must be used within InputNumberRoot',
    );
  }
  return context;
};

export interface InputNumberRootProps {
  /**
   * Whether to render the number input as a child component.
   * @default false
   */
  asChild?: boolean;
  value?: number | '';
  defaultValue?: number;
  /**
   * The unit of the number input.
   */
  unit?: string;
  /**
   * The minimum value of the number input.
   */
  min?: number;
  /**
   * The maximum value of the number input.
   */
  max?: number;
  /**
   * The step of the number input.
   * @default 1
   */
  step?: number;
  onChange?: (value: number) => void;
  className?: string;
}

const InputNumberRoot = forwardRef<
  HTMLDivElement,
  InputNumberRootProps & React.PropsWithChildren
>(
  (
    {
      asChild = false,
      children,
      value: propValue,
      defaultValue,
      min,
      max,
      step = 1,
      unit,
      onChange: onValueChange,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';
    const prevPropValueRef = useRef(propValue);

    const [internalValue, setInternalValue] = useState<number | ''>(
      propValue ?? defaultValue ?? '',
    );

    // Use controlled state if open prop is provided, otherwise use internal state
    const finalValue = propValue !== undefined ? propValue : internalValue;

    const handleValueChange = useCallback(
      (value: number | '' | ((prev: number | '') => number | '')) => {
        const newValue =
          typeof value === 'function' ? value(finalValue) : value;

        if (propValue === undefined) {
          // Only update internal state if not controlled
          setInternalValue(newValue);
        }

        // Always call onOpenChange if provided
        onValueChange?.(Number(newValue));
      },
      [finalValue, propValue, onValueChange],
    );

    // Sync with controlled prop value
    useEffect(() => {
      if (
        typeof propValue === 'number' &&
        propValue !== prevPropValueRef.current
      ) {
        setInternalValue(propValue);
        prevPropValueRef.current = propValue;
      }
    }, [propValue]);

    const contextValue: InputNumberContextValue = {
      value: internalValue,
      min,
      max,
      step,
      unit,
      onValueChange: handleValueChange,
    };

    return (
      <InputNumberContext.Provider value={contextValue}>
        <Comp
          ref={ref}
          className={cn('flex items-center gap-2', className)}
          {...props}
        >
          {children}
        </Comp>
      </InputNumberContext.Provider>
    );
  },
);
InputNumberRoot.displayName = 'InputNumberRoot';

export interface DragWheelControlsProps {
  /**
   * Whether to render the drag and wheel controls as a child component.
   * @default false
   */
  asChild?: boolean;
  /**
   * Whether to enable drag functionality.
   * @default false
   */
  disableDrag?: boolean;
  /**
   * Whether to enable wheel functionality.
   * @default false
   */
  disableWheel?: boolean;
  /**
   * Scale factor for drag sensitivity. Higher values make dragging more sensitive.
   * @default 1
   */
  dragScale?: number;
  /**
   * Scale factor for wheel sensitivity. Higher values make wheel scrolling more sensitive.
   * @default 1
   */
  wheelScale?: number;
  /**
   * When true, disables ALT key 0.1 multiplier and snaps value to multiples of step.
   * @default false
   */
  lockToStep?: boolean;
  /**
   * Drag axis. Vertical: top is positive. Horizontal: right is positive.
   * @default 'vertical'
   */
  dragDirection?: 'vertical' | 'horizontal';
}

const DragWheelControls = forwardRef<
  HTMLDivElement,
  DragWheelControlsProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      children,
      asChild = false,
      disableDrag = false,
      disableWheel = false,
      dragScale = 1,
      wheelScale = 1,
      lockToStep = false,
      dragDirection = 'vertical',
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';
    const { value, onValueChange, min, max, step } = useInputNumberContext();
    const localRef = useRef<HTMLDivElement>(null);

    const currentValue = typeof value === 'number' ? value : (min ?? 0);

    // Handle wheel increment
    useEffect(() => {
      const container = localRef.current;
      if (!container || disableWheel) {
        return;
      }

      const handleWheel = (event: WheelEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const direction = event.deltaY < 0 ? 1 : -1;
        const threshold = Math.max(1, Math.round(wheelScale));

        const anyContainer = container as unknown as { __wheelAccum?: number };
        anyContainer.__wheelAccum =
          (anyContainer.__wheelAccum ?? 0) + direction;
        const stepsCount = (anyContainer.__wheelAccum / threshold) | 0;
        if (stepsCount === 0) {
          return;
        }
        anyContainer.__wheelAccum -= stepsCount * threshold;

        const increment = stepsCount * step;
        const newValue = Math.round((currentValue + increment) / step) * step;
        const clampedValue = Math.min(
          max ?? Infinity,
          Math.max(min ?? -Infinity, newValue),
        );
        onValueChange(clampedValue);
      };

      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }, [disableWheel, currentValue, step, wheelScale, min, max, onValueChange]);

    const { handleMouseDown, isDragging } = useInputDragControl({
      containerRef: localRef,
      value: currentValue,
      step,
      min,
      max,
      onDrag: onValueChange,
      dragScale,
      lockToStep,
      dragDirection,
    });

    return (
      <Comp
        ref={node => {
          (localRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onMouseDown={!disableDrag ? handleMouseDown : undefined}
        style={{
          cursor: isDragging
            ? dragDirection === 'horizontal'
              ? 'ew-resize'
              : 'ns-resize'
            : 'default',
        }}
        className={cn('flex items-center gap-2', className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
DragWheelControls.displayName = 'DragWheelControls';

const InputNumberBase = forwardRef<
  HTMLInputElement,
  Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'min' | 'max' | 'step'
  >
>(({ className, ...props }, ref) => {
  const { value, onValueChange, min, max, step, unit } =
    useInputNumberContext();

  const inputRef = useRef<HTMLInputElement>(null);

  // Local display value that updates on every keystroke
  const [displayValue, setDisplayValue] = useState<string>(
    value === '' ? '' : String(value),
  );

  // Track if input is focused to prevent external updates while typing
  const isFocusedRef = useRef(false);

  // Sync display value when context value changes (from external sources like drag/wheel)
  // but only when the input is not focused
  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayValue(value === '' ? '' : String(value));
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Only update display value, don't fire onValueChange
      setDisplayValue(event.target.value);
    },
    [],
  );

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    const trimmed = displayValue.trim();

    // If empty or not a valid number, don't fire onChange
    if (trimmed === '' || isNaN(Number(trimmed))) {
      setDisplayValue('');
      return;
    }

    let numValue = Number(trimmed);

    // Clamp the value
    numValue = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, numValue));

    setDisplayValue(String(numValue));
    onValueChange(numValue);
  }, [displayValue, min, max, onValueChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const direction = event.key === 'ArrowUp' ? 'up' : 'down';
        const increment = direction === 'up' ? step : -step;
        // Use displayValue if it's a valid number, otherwise fallback to context value or min
        const currentDisplayNum = Number(displayValue);
        const currentValue = !isNaN(currentDisplayNum)
          ? currentDisplayNum
          : typeof value === 'number'
            ? value
            : (min ?? 0);
        const newValue = Math.round((currentValue + increment) / step) * step;
        const clampedValue = Math.min(
          max ?? Infinity,
          Math.max(min ?? -Infinity, newValue),
        );
        // Update both display and fire onChange for arrow keys
        setDisplayValue(String(clampedValue));
        onValueChange(clampedValue);
      }
    },
    [displayValue, value, step, min, max, onValueChange],
  );

  return (
    <Input
      ref={ref || inputRef}
      type="number"
      value={displayValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      min={min}
      max={max}
      step={step}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={typeof value === 'number' ? value : undefined}
      aria-valuetext={
        typeof value === 'number'
          ? `${value}${unit ? ` ${unit}` : ''}`
          : undefined
      }
      className={cn('flex-1', className)}
      {...props}
    />
  );
});
InputNumberBase.displayName = 'InputNumberBase';

export interface InputNumberUnitProps {
  /**
   * Whether to render the number input unit as a child component.
   * @default false
   */
  asChild?: boolean;
}

const InputNumberUnit = forwardRef<
  HTMLDivElement,
  InputNumberUnitProps & React.HTMLAttributes<HTMLDivElement>
>(({ className, children, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';
  const { unit } = useInputNumberContext();

  const displayUnit = children || unit;

  if (!displayUnit) {
    return null;
  }

  return (
    <Comp
      ref={ref}
      className={cn(
        'text-muted-foreground text-sm whitespace-nowrap',
        className,
      )}
      {...props}
    >
      {displayUnit}
    </Comp>
  );
});
InputNumberUnit.displayName = 'InputNumberUnit';

/* _______Higher Level Component________ */

export interface InputNumberProps
  extends Omit<InputNumberRootProps, 'children' | 'asChild'> {
  controls?: DragWheelControlsProps;
  base?: React.ComponentProps<typeof InputNumberBase>;
  placeholder?: string;
  disabled?: boolean;
}

const InputNumber: React.FC<InputNumberProps> = ({
  controls = {},
  base = {},
  placeholder,
  disabled,
  ...props
}) => {
  return (
    <InputNumberRoot {...props} asChild>
      <DragWheelControls {...controls} disableWheel={disabled}>
        <InputNumberBase
          {...base}
          placeholder={placeholder}
          disabled={disabled}
        />
        <InputNumberUnit />
      </DragWheelControls>
    </InputNumberRoot>
  );
};

export {
  DragWheelControls,
  InputNumber,
  InputNumberBase,
  InputNumberRoot,
  InputNumberUnit,
};
